import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/redux';
import { selectUser, selectIsSuperAdmin, selectIsAdmin } from '../store/slices/authSlice';
import { User, Role, CreateUserRequest, UpdateUserRequest } from '../types/auth';
import { authService } from '../services/authService';
import PermissionGate from '../components/Common/PermissionGate';
import { usePermission } from '../hooks/usePermissions';

const UserManagementPage: React.FC = () => {
  const currentUser = useAppSelector(selectUser);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const isAdmin = useAppSelector(selectIsAdmin);

  // Permission checks
  const canViewUsers = usePermission('users:read');
  const canCreateUsers = usePermission('users:create');
  const canUpdateUsers = usePermission('users:update');
  const canDeleteUsers = usePermission('users:delete');
  const canManageUsers = usePermission('users:manage');
  const canAssignRoles = usePermission('users:manage'); // Use existing permission instead

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roleIds: [],
  });

  const [editForm, setEditForm] = useState<UpdateUserRequest>({
    firstName: '',
    lastName: '',
    email: '',
    isActive: true,
    roleIds: [],
  });
  useEffect(() => {
    if (canViewUsers) {
      loadData();
    }
  }, [canViewUsers]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, rolesData] = await Promise.all([
        authService.getUsers(),
        authService.getRoles(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      await authService.createUser(createForm);
      setIsCreateDialogOpen(false);
      setCreateForm({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        roleIds: [],
      });
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await authService.updateUser(selectedUser.id, editForm);
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      setEditForm({});
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await authService.deleteUser(selectedUser.id);
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      isActive: user.isActive,
      roleIds: user.roles.map(r => r.id),
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const canManageUser = (user: User): boolean => {
    if (!currentUser) return false;
    
    // SuperAdmin can manage anyone
    if (authService.isSuperAdmin(currentUser)) return true;
    
    // Admin can manage non-SuperAdmin users
    if (authService.isAdmin(currentUser)) {
      return !authService.isSuperAdmin(user);
    }
    
    return false;
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'SuperAdmin': return 'error';
      case 'Admin': return 'warning';
      case 'Manager': return 'primary';
      case 'Operator': return 'secondary';
      case 'Viewer': return 'default';
      default: return 'default';
    }
  };
  const getRoleIcon = (roleName: string) => {
    switch (roleName) {
      case 'SuperAdmin': return <SupervisorIcon fontSize="small" />;
      case 'Admin': return <AdminIcon fontSize="small" />;
      default: return undefined;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <PermissionGate permission="users:read" fallback={
      <Alert severity="error" sx={{ mt: 2 }}>
        You don't have permission to manage users.
      </Alert>
    }>
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                User Management
              </Typography>
              <PermissionGate permissions={["users:create", "users:manage"]}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  Create User
                </Button>
              </PermissionGate>
            </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          @{user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.roles.map((role) => (
                          <Chip
                            key={role.id}
                            label={role.name}
                            size="small"
                            color={getRoleColor(role.name) as any}
                            icon={getRoleIcon(role.name)}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canManageUser(user) && (
                          <>
                            <PermissionGate permissions={["users:update", "users:manage"]}>
                              <Tooltip title="Edit User">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditDialog(user)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                            <PermissionGate permissions={["users:delete", "users:manage"]}>
                              <Tooltip title="Delete User">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => openDeleteDialog(user)}
                                  disabled={user.id === currentUser?.id}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon />
            Create New User
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="First Name"
                value={createForm.firstName}
                onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                required
              />
              <TextField
                label="Last Name"
                value={createForm.lastName}
                onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                required
              />
            </Box>
            <TextField
              label="Username"
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              required
            />
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
            />
            <TextField
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              required
            />
            <FormControl>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={createForm.roleIds}
                onChange={(e) => setCreateForm({ ...createForm, roleIds: e.target.value as string[] })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((roleId) => {
                      const role = roles.find(r => r.id === roleId);
                      return role ? (
                        <Chip
                          key={roleId}
                          label={role.name}
                          size="small"
                          color={getRoleColor(role.name) as any}
                        />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {roles
                  .filter(role => {
                    // SuperAdmin can assign any role, Admin cannot assign SuperAdmin
                    if (role.name === 'SuperAdmin') {
                      return isSuperAdmin;
                    }
                    return true;
                  })
                  .map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Checkbox checked={createForm.roleIds.includes(role.id)} />
                      <ListItemText primary={role.name} secondary={role.description} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="First Name"
                value={editForm.firstName || ''}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                value={editForm.lastName || ''}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Box>
            <TextField
              label="Username"
              value={editForm.username || ''}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive || false}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControl>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={editForm.roleIds || []}
                onChange={(e) => setEditForm({ ...editForm, roleIds: e.target.value as string[] })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((roleId) => {
                      const role = roles.find(r => r.id === roleId);
                      return role ? (
                        <Chip
                          key={roleId}
                          label={role.name}
                          size="small"
                          color={getRoleColor(role.name) as any}
                        />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {roles
                  .filter(role => {
                    // SuperAdmin can assign any role, Admin cannot assign SuperAdmin
                    if (role.name === 'SuperAdmin') {
                      return isSuperAdmin;
                    }
                    return true;
                  })
                  .map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Checkbox checked={(editForm.roleIds || []).includes(role.id)} />
                      <ListItemText primary={role.name} secondary={role.description} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">Update User</Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.username}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>        </DialogActions>
      </Dialog>
      </Box>
    </PermissionGate>
  );
};

export default UserManagementPage;
